import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    GridColDef,
    gridClasses,
    gridColumnFieldsSelector,
    gridColumnLookupSelector,
    gridColumnVisibilityModelSelector,
    useGridApiContext,
    useGridRootProps,
    useGridSelector,
} from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";
import { FormControlLabel, Tooltip } from "@mui/material";
import { inputBaseClasses } from "@mui/material/InputBase";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { DragIndicatorRounded, LockRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { ColumnManagementContext } from "./columnManagementContext";
import { moveField } from "../../utils/tableColumnLayout";

// Same slots as MUI's GridColumnsManagement, so theme overrides keep applying.
const Header = styled("div", {
    name: "MuiDataGrid",
    slot: "ColumnsManagementHeader",
    overridesResolver: (props, styles) => styles.columnsManagementHeader,
})(({ theme }) => ({
    padding: theme.spacing(1.5, 3),
}));

const SearchInputSlot = styled("div")(({ theme }) => ({
    [`& .${inputBaseClasses.root}`]: {
        padding: theme.spacing(0, 1.5),
    },
    // No native clear icon next to our own
    [`& .${inputBaseClasses.input}::-webkit-search-cancel-button`]: {
        display: "none",
    },
}));

const Body = styled("div", {
    name: "MuiDataGrid",
    slot: "ColumnsManagement",
    overridesResolver: (props, styles) => styles.columnsManagement,
})(({ theme }) => ({
    padding: theme.spacing(0, 1.5, 1.5, 1),
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    flex: "1 1",
    maxHeight: 340,
}));

const Footer = styled("div", {
    name: "MuiDataGrid",
    slot: "ColumnsManagementFooter",
    overridesResolver: (props, styles) => styles.columnsManagementFooter,
})(({ theme }) => ({
    padding: theme.spacing(0.5, 1, 0.5, 3),
    display: "flex",
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const Hint = styled("div")(({ theme }) => ({
    padding: theme.spacing(0.5, 1.5),
    color: theme.palette.grey[600],
    fontSize: theme.typography.caption.fontSize,
}));

const Row = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    "&[data-dragging='true']": {
        position: "relative",
        zIndex: 1,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
    },
}));

const DragHandle = styled("span")(({ theme }) => ({
    display: "inline-flex",
    padding: theme.spacing(0.25),
    color: theme.palette.grey[500],
    borderRadius: theme.shape.borderRadius,
    cursor: "grab",
    touchAction: "none",
    "&:hover": { color: theme.palette.text.primary },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
}));

// Keeps rows aligned while searching, when the drag handles are hidden
const HandleSpacer = styled("span")({ display: "inline-block", width: 24 });

interface RowProps {
    column: GridColDef;
    visible: boolean;
    dragDisabled: boolean;
    onToggle: (field: string, visible: boolean) => void;
}

const SortableColumnRow = ({ column, visible, dragDisabled, onToggle }: RowProps) => {
    const { t } = useTranslation();
    const rootProps = useGridRootProps();
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
        useSortable({ id: column.field, disabled: dragDisabled });
    const locked = column.hideable === false;

    return (
        <Row
            ref={setNodeRef}
            style={{ transform: CSS.Translate.toString(transform), transition }}
            data-dragging={isDragging}
            data-testid={`columns-panel-row-${column.field}`}
            className={gridClasses.columnsManagementRow}
        >
            {dragDisabled ? (
                <HandleSpacer/>
            ) : (
                <DragHandle
                    ref={setActivatorNodeRef}
                    {...attributes}
                    {...listeners}
                    aria-label={t("Drag to reorder")}
                >
                    <DragIndicatorRounded fontSize="small"/>
                </DragHandle>
            )}
            <FormControlLabel
                sx={{ flex: 1, mr: 0 }}
                control={
                    <rootProps.slots.baseCheckbox
                        checked={locked || visible}
                        disabled={locked}
                        onChange={() => onToggle(column.field, !visible)}
                        name={column.field}
                        sx={{ p: 0.5 }}
                        {...rootProps.slotProps?.baseCheckbox}
                    />
                }
                label={column.headerName || column.field}
            />
            {locked && (
                <Tooltip title={t("Always visible")}>
                    <LockRounded fontSize="small" sx={{ color: "grey.500", mr: 0.5 }}/>
                </Tooltip>
            )}
        </Row>
    );
};

const NO_FIELDS: string[] = [];

const matchesSearch = (column: GridColDef, search: string) =>
    (column.headerName || column.field).toLowerCase().includes(search.toLowerCase());

/**
 * Columns panel with drag-and-drop reordering, used by `Table` when it gets a
 * `columnManagement`. Lists the columns in table order; non-hideable columns can be
 * moved but not hidden, fixed columns (row actions) are not listed at all.
 */
const SortableColumnsManagement = () => {
    const { t } = useTranslation();
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const columnManagement = useContext(ColumnManagementContext);
    const lookup = useGridSelector(apiRef, gridColumnLookupSelector);
    const gridOrder = useGridSelector(apiRef, gridColumnFieldsSelector);
    const visibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
    const [search, setSearch] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    // The order comes from the table's state rather than the grid: right after a drop the
    // grid takes over the new columns only in its next effect, which would flicker.
    const order = columnManagement?.order ?? gridOrder;
    const fixedFields = columnManagement?.fixedFields ?? NO_FIELDS;
    const columns = useMemo(
        () => order.filter((field) => lookup[field] && !fixedFields.includes(field)).map((field) => lookup[field]),
        [order, fixedFields, lookup]
    );
    const shownColumns = search ? columns.filter((column) => matchesSearch(column, search)) : columns;
    const dragDisabled = !columnManagement || !!search;

    const hideableColumns = columns.filter((column) => column.hideable !== false);
    const allVisible = hideableColumns.every((column) => visibilityModel[column.field] !== false);
    const allHidden = hideableColumns.every((column) => visibilityModel[column.field] === false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!columnManagement || !over || active.id === over.id) return;
        columnManagement.onOrderChange(moveField(order, String(active.id), String(over.id), fixedFields));
    };

    const toggleColumn = (field: string, visible: boolean) => {
        apiRef.current.setColumnVisibility(field, visible);
    };

    const toggleAll = () => {
        const next = { ...visibilityModel };
        hideableColumns.forEach((column) => {
            next[column.field] = !allVisible;
        });
        apiRef.current.setColumnVisibilityModel(next);
    };

    return (
        <>
            <Header className={gridClasses.columnsManagementHeader}>
                <SearchInputSlot>
                    <rootProps.slots.baseTextField
                        placeholder={t("Search")}
                        inputRef={searchInputRef}
                        className={gridClasses.columnsManagementSearchInput}
                        value={search}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
                        variant="outlined"
                        size="small"
                        type="search"
                        autoComplete="off"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <rootProps.slots.baseInputAdornment position="start">
                                    <rootProps.slots.quickFilterIcon/>
                                </rootProps.slots.baseInputAdornment>
                            ),
                            endAdornment: (
                                <rootProps.slots.baseIconButton
                                    aria-label={t("Reset")}
                                    size="small"
                                    sx={{ visibility: search ? "visible" : "hidden" }}
                                    tabIndex={-1}
                                    onClick={() => {
                                        setSearch("");
                                        searchInputRef.current?.focus();
                                    }}
                                    {...rootProps.slotProps?.baseIconButton}
                                >
                                    <rootProps.slots.quickFilterClearIcon fontSize="small"/>
                                </rootProps.slots.baseIconButton>
                            ),
                        }}
                        inputProps={{ "aria-label": t("Search") }}
                        {...rootProps.slotProps?.baseTextField}
                    />
                </SearchInputSlot>
            </Header>
            <Body className={gridClasses.columnsManagement}>
                {search && columnManagement && shownColumns.length > 0 && (
                    <Hint>{t("Clear the search to reorder columns")}</Hint>
                )}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={shownColumns.map((column) => column.field)} strategy={verticalListSortingStrategy}>
                        {shownColumns.map((column) => (
                            <SortableColumnRow
                                key={column.field}
                                column={column}
                                visible={visibilityModel[column.field] !== false}
                                dragDisabled={dragDisabled}
                                onToggle={toggleColumn}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
                {shownColumns.length === 0 && <Hint>{t("No columns found")}</Hint>}
            </Body>
            <Footer className={gridClasses.columnsManagementFooter}>
                <FormControlLabel
                    control={
                        <rootProps.slots.baseCheckbox
                            disabled={hideableColumns.length === 0}
                            checked={allVisible}
                            indeterminate={!allVisible && !allHidden}
                            onChange={toggleAll}
                            name="showHideAll"
                            sx={{ p: 0.5 }}
                            {...rootProps.slotProps?.baseCheckbox}
                        />
                    }
                    label={t("Show/Hide all")}
                />
                {columnManagement && (
                    <rootProps.slots.baseButton
                        {...rootProps.slotProps?.baseButton}
                        onClick={columnManagement.onReset}
                        disabled={!columnManagement.canReset}
                    >
                        {t("Reset")}
                    </rootProps.slots.baseButton>
                )}
            </Footer>
        </>
    );
};

export default SortableColumnsManagement;
